import PropTypes from 'prop-types';
import React from 'react';
import SortSelect from '../../Resources/SortSelect/SortSelect';
import Arrow from '../Arrow/Arrow';

import './ResponsiveTable.scss';

export default class ResponsiveTable extends React.PureComponent {
    constructor(props) {
        super(props);

        this.baseCls = 'component-Elements-ResponsiveTable';

        this.state = {
            openedMenu: null,
        };
    }

    static propTypes = {
        /**
         * array of header objects for table header
         */
        tableHeaders: PropTypes.arrayOf(
            PropTypes.shape({
                label: PropTypes.string.isRequired,
                sortOptions: PropTypes.arrayOf(
                    PropTypes.shape({
                        label: PropTypes.string,
                        value: PropTypes.oneOf(['asc', 'desc']),
                    })
                ),
                value: PropTypes.string,
            })
        ).isRequired,
        /**
         * array of row data for table rows
         */
        rowData: PropTypes.arrayOf(PropTypes.array).isRequired,
        /**
         * Optional class to add
         */
        className: PropTypes.string,
        /**
         * whether display ":" in mobile
         */
        showColonInMobile: PropTypes.bool,
        onSort: PropTypes.func,
        sortType: PropTypes.string,
        sortOrder: PropTypes.string,
        deactivatedRows: PropTypes.array,
    };

    static defaultProps = {
        className: '',
        onSort: () => {},
        sortType: '',
        sortOrder: '',
        showColonInMobile: true,
        deactivatedRows: [],
    };

    getRowData = (data, label) => {
        if (!data) {
            return 'N/A';
        }

        if (data.median || data.median === 0) {
            return data.median;
        }

        if (Array.isArray(data)) {
            return data.map(this.renderRowDataList(label));
        }

        return data;
    };

    renderRowDataList = (label) => (data, index) => {
        if (label === 'Comments') {
            return <li key={`row-data-list-${index}`}>{data}</li>;
        }

        return <div key={`row-data-list-${index}`}>{data}</div>;
    };

    handleTipClick = (companyId, companyName) => () => {
        this.props.onTipClick(companyId, companyName);
    };

    handleSortMenuClick = (index) => () => this.setState({ openedMenu: index });

    handleSortOptionClick = (value, option) => () => {
        this.props.onSort(value, option);
        this.handleSortMenuClose();
    };

    handleSortMenuClose = () => {
        this.setState({ openedMenu: null });
    };

    renderTableHeader = (
        { label, sublabel, sortOptions = [], value },
        index
    ) => {
        const { sortType, sortOrder } = this.props;
        const isSortable = !!sortOptions.length;

        const isSortMenuOpened = isSortable && this.state.openedMenu === index;

        const sortOptionsWithOnClick = sortOptions.map(
            ({ label, value: optionValue }) => {
                return {
                    option: label,
                    onClick: this.handleSortOptionClick(value, optionValue),
                    selected: sortType === value && sortOrder === optionValue,
                };
            }
        );

        return (
            <th scope="col" key={`table-header-${index}`}>
                <span className="header-sublabel">{sublabel}</span>
                <span
                    className={isSortable ? 'is-sortable' : ''}
                    onClick={this.handleSortMenuClick(index)}
                >
                    {label} {isSortable && <Arrow direction="down" />}
                </span>
                {isSortMenuOpened && (
                    <SortSelect
                        sortOptions={sortOptionsWithOnClick}
                        onClose={this.handleSortMenuClose}
                        isDropdownOpen={isSortMenuOpened}
                    />
                )}
            </th>
        );
    };

    renderTableRow = (row, index) => {
        const deactivatedClassName = this.props.deactivatedRows.includes(index)
            ? 'deactivated'
            : '';
        return (
            <tr key={`table-row-${index}`} className={deactivatedClassName}>
                {row.map(this.renderTableItem)}
            </tr>
        );
    };

    renderTableItem = (rowItem, index) => {
        const label =
            (this.props.tableHeaders[index] &&
                this.props.tableHeaders[index].label) ||
            '';
        const tdContent = this.getRowData(
            rowItem,
            this.props.tableHeaders[index].label
        );
        const cssClass = label
            .replace(/[!\"#$%&'\(\)\*\+,\.\/:;<=>\?\@\[\\\]\^`\{\|\}~]/g, '')
            .replace(/\s/g, '-')
            .toLowerCase();
        const { showColonInMobile } = this.props;

        return (
            <td
                className={`${this.baseCls}__${cssClass}`}
                key={`table-row-${index}`}
            >
                {label && (
                    <h6 className={`${this.baseCls}__mobile-label`}>
                        {`${label}${showColonInMobile ? ':' : ''}`}
                    </h6>
                )}
                {(index === 0 && <h2>{tdContent}</h2>) || tdContent}
            </td>
        );
    };

    renderNoResultText = () => {
        return <p className="no-results">There are no matching results.</p>;
    };

    renderTable = () => {
        const {
            baseCls,
            props: { tableHeaders, rowData },
        } = this;

        const hasResults = !!rowData.length;
        const colSpan = tableHeaders.length.toString();

        return (
            <table className={`${baseCls}__table desktop-table`}>
                <thead className="thead dark">
                    <tr>{tableHeaders.map(this.renderTableHeader)}</tr>
                </thead>

                <tbody>
                    {hasResults ? (
                        rowData.map(this.renderTableRow)
                    ) : (
                        <tr>
                            <td colSpan={colSpan}>
                                {this.renderNoResultText()}
                            </td>
                        </tr>
                    )}
                </tbody>
            </table>
        );
    };

    render() {
        return (
            <div className={`${this.baseCls} ${this.props.className}`}>
                {this.renderTable()}
            </div>
        );
    }
}
